import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useCancellableRequest } from '../../services/cancellable-request/hook';
import HttpError from '../../services/http-service/HttpError';
import { ApiCall } from '../../services/http-service/types/ApiCall';
import { isShallowEqual } from './utils';

export interface Options<CallResponse, ModifiedData = undefined, SkippedInitLoad extends boolean | undefined = false> {
  responseModifier?: (data: CallResponse) => ModifiedData;
  restrictEmptyParams?: SkippedInitLoad;
}

/**
 * States of request hook
 */
type DefaultStates<DataT> =
  | {
    loading: true;
    error: null;
    data: null;
  }
  | {
    loading: false;
    error: HttpError;
    data: null;
  }
  | {
    loading: false;
    error: null;
    data: DataT;
  }
  //next 2 states can be reached by triggering refetch manually
  | {
    loading: true;
    error: null;
    data: DataT;
  }
  | {
    loading: true;
    error: HttpError;
    data: null;
  };

/**
 * States of request hook when initial load is skipped by restictEmptyParams property.
 */
type SkippedInitLoadStates<DataT> = {
  loading: false;
  error: null;
  data: null;
} | DefaultStates<DataT>;

type LodingStates<DataT, SkippedInitLoad> =
  (SkippedInitLoad extends true ? SkippedInitLoadStates<DataT> : DefaultStates<DataT>);

export type UseRequestReturnValue<DataT, CallParams, SkippedInitLoad> = {
  setData: Dispatch<SetStateAction<DataT | null>>;
  trigger: (params: CallParams) => Promise<void>;
} & LodingStates<DataT, SkippedInitLoad>;

const useRequest = <CallParams, CallResponse, ModifiedData = undefined, SkippedInitLoad extends boolean | undefined = undefined>(
  apiCall: ApiCall<CallParams, CallResponse>,
  params: CallParams | null,
  options?: Options<CallResponse, ModifiedData, SkippedInitLoad>
) => {
  type HasModifier = ModifiedData extends undefined ? false : true;
  type Data = HasModifier extends true ? ModifiedData : CallResponse;

  const [loading, setLoading] = useState(!options?.restrictEmptyParams || !!params);
  const [error, setError] = useState<HttpError | null>(null);
  const [data, setData] = useState<Data | null>(null);
  const request = useCancellableRequest();
  const prevParamsRef = useRef<{ params: CallParams } | null>(null);

  const fetch = useCallback(
    async (requestParams: CallParams): Promise<void> => {
      setLoading(true);

      try {
        const data = await request.send(apiCall, requestParams);

        if (options?.responseModifier) {
          const modifiedData = options.responseModifier(data) as Data;
          setData(modifiedData);
          setLoading(false);
        } else {
          const rawData = data as Data;
          setData(rawData);
          setLoading(false);
        }
        setError(null);
      } catch (error) {
        if (HttpError.isHttpError(error)) {
          setError(error);
          setLoading(false);
        }
      }

      return undefined as never;
    },
    [apiCall]
  );

  useEffect(() => {
    const allowToFetchWithEmptyParams = !options?.restrictEmptyParams;
    const shouldFetch = !params && allowToFetchWithEmptyParams || !!params;
    if (shouldFetch && (!prevParamsRef.current || !isShallowEqual(prevParamsRef.current?.params, params))) {
      prevParamsRef.current = { params: params as CallParams };
      fetch(params as CallParams);
    }
  }, [params, options?.restrictEmptyParams, fetch]);

  return {
    data,
    loading,
    error,
    setData,
    trigger: fetch
  } as UseRequestReturnValue<Data, CallParams, SkippedInitLoad>;
};

export default useRequest;
